import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useBranding } from "../lib/branding";
import { toast } from "sonner";

const MX_GRADIENT = { background: "linear-gradient(135deg, #6C47FF 0%, #E91E8C 100%)" };

export default function ResetPassword() {
  const branding = useBranding();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) return toast.error("Password must be at least 6 characters");
    if (newPassword !== confirm) return toast.error("Passwords do not match");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: newPassword });
      toast.success("Password updated. You can now sign in.");
      navigate("/login");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel — Form */}
      <div className="flex-1 bg-[#F0F1F7] flex flex-col justify-center px-10 py-12 lg:px-16 max-w-[560px]">
        <div className="mb-10">
          {branding.logo_data ? (
            <img src={branding.logo_data} alt={branding.name} className="h-12 w-auto max-w-[200px] object-contain" />
          ) : (
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-[10px] flex items-center justify-center text-white font-[900] text-lg flex-shrink-0"
                style={MX_GRADIENT}
              >
                M
              </div>
              <div>
                <div className="text-[15px] font-[800] tracking-tight text-[#1A1A2E] uppercase leading-none">
                  {branding.name}
                </div>
                <div className="text-[9px] font-[600] text-[#9CA3AF] tracking-[0.18em] uppercase mt-1">
                  Learning Hub
                </div>
              </div>
            </div>
          )}
        </div>

        <h1 className="text-[34px] font-[900] text-[#1A1A2E] leading-tight mb-2">Set new password.</h1>
        <p className="text-sm text-[#6B7280] mb-8">Choose a strong password — at least 6 characters.</p>

        {!token ? (
          <div className="space-y-5" data-testid="reset-password-no-token">
            <div className="bg-red-50 rounded-[12px] border border-red-200 p-5 text-sm text-red-700">
              This reset link is missing its token. Request a new one.
            </div>
            <Link
              to="/forgot-password"
              className="flex items-center justify-center w-full text-white rounded-[12px] min-h-[52px] py-3 font-[700] text-sm"
              style={MX_GRADIENT}
            >
              Request a new link
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5" data-testid="reset-password-form">
            <div>
              <label className="block text-[10px] font-[700] text-[#6B7280] mb-2 uppercase tracking-[0.12em]">
                New Password
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                data-testid="reset-password-new"
                className="w-full rounded-[10px] border border-[#E8EAEF] px-4 py-3 min-h-[48px] bg-white focus:outline-none focus:border-[#6C47FF] text-sm"
              />
            </div>
            <div>
              <label className="block text-[10px] font-[700] text-[#6B7280] mb-2 uppercase tracking-[0.12em]">
                Confirm New Password
              </label>
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                data-testid="reset-password-confirm"
                className="w-full rounded-[10px] border border-[#E8EAEF] px-4 py-3 min-h-[48px] bg-white focus:outline-none focus:border-[#6C47FF] text-sm"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="reset-password-submit"
              className="w-full text-white rounded-[12px] min-h-[52px] py-3 font-[700] text-sm disabled:opacity-60"
              style={MX_GRADIENT}
            >
              {loading ? "Updating…" : "Update Password"}
            </button>
            <Link
              to="/login"
              className="block text-center text-sm font-[600] text-[#6C47FF] hover:underline"
            >
              Back to Sign in
            </Link>
          </form>
        )}
      </div>

      {/* Right Panel — Hero */}
      <div
        className="hidden lg:flex flex-1 flex-col justify-center px-16 py-12 text-white relative overflow-hidden"
        style={MX_GRADIENT}
      >
        <div className="absolute top-[-80px] right-[-80px] w-[320px] h-[320px] rounded-full bg-white/10 pointer-events-none" />
        <div className="absolute bottom-[-60px] left-[-60px] w-[240px] h-[240px] rounded-full bg-white/10 pointer-events-none" />
        <div className="relative z-10">
          <h2 className="text-[48px] font-[900] leading-[1.05] mb-6">
            Almost<br />there.
          </h2>
          <p className="text-white/80 text-base leading-relaxed max-w-sm">
            Your reset link is single-use. Once your password is updated, sign in with your new credentials.
          </p>
        </div>
      </div>
    </div>
  );
}
